import * as Sentry from '@sentry/react'
import { ClofoApiError } from '@clofo/data/api'

const THIRD_PARTY_IGNORE_ERRORS: Array<string | RegExp> = [
  // Browser fetch errors from network drops, tab closes, or extensions
  'Failed to fetch', // Chrome, Edge
  'Load failed', // Safari
  'NetworkError when attempting to fetch resource', // Firefox

  // A raw `visibilitychange` DOM Event used as a promise rejection reason (CF-OVERVIEW-REACT-95).
  // Nothing in our bundle or the third-party scripts we load rejects with the event, so this comes
  // from browser extensions / embedded browsers; it has no stack trace and nothing actionable.
  /Event `Event` \(type=visibilitychange\) captured as promise rejection/,
]

class Logger {
  init(options: Parameters<typeof Sentry.init>[0]) {
    return Sentry.init({
      ...options,
      ignoreErrors: [...THIRD_PARTY_IGNORE_ERRORS, ...(options?.ignoreErrors ?? [])],
    })
  }
  addSentryBreadcrumb = Sentry.addBreadcrumb
  setUser = Sentry.setUser
  error(
    error: unknown,
    options?: {
      tags?: Record<string, string | number | boolean | undefined>
      extra?: Record<string, unknown>
    },
  ) {
    console.error(error)
    if (error instanceof ClofoApiError) {
      Sentry.captureException(error, {
        tags: {
          endpoint: error.endpoint,
          status: error.status,
          errorCode: error.errorCode,
        },
      })
    } else {
      Sentry.captureException(error, {
        tags: options?.tags,
        extra: options?.extra,
      })
    }
  }
}

export const logger = new Logger()

const AWS_FILENAME_KEY_REGEX = /response-content-disposition/i
const REDACTED = '[REDACTED]'

export const scrubAwsFilenameFromString = (value: string) => {
  const match = value.match(AWS_FILENAME_KEY_REGEX)

  if (!match || typeof match.index !== 'number') {
    return value
  }

  const prefix = value.slice(0, match.index)
  return `${prefix}response-content-disposition=${REDACTED}`
}

const scrubFileNameOnObject = (value: any) => {
  if (!value || typeof value !== 'object') {
    return value
  }

  if (value.fileName) {
    return { ...value, fileName: REDACTED }
  }

  return value
}

type EventWithRequestUrl = {
  request?: {
    url?: string
  }
}

type BreadcrumbWithUrlData = {
  category?: string
  message?: string
  data?: {
    url?: string
    arguments?: any[]
    request?: {
      url?: string
    }
  }
}

// Builds a short data-test attribute suffix from the nearest element in the target chain
// that actually has data-test-* attributes.
// Example from the dealroom DMS tracking card:
// [data-test-dropdown-button="dms-details-actions"]
const buildSafeUiBreadcrumbSuffix = (event: Event | undefined): string | undefined => {
  const target = event?.target

  const escapeSelectorValue = (value: string) => {
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
      return CSS.escape(value)
    }

    return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')
  }

  if (
    typeof Node === 'undefined' ||
    typeof HTMLElement === 'undefined' ||
    !(target instanceof Node)
  ) {
    return undefined
  }

  const getDataAttributeSegment = (element: HTMLElement) => {
    const selector: string[] = []

    for (const name of element.getAttributeNames()) {
      if (!name.startsWith('data-test-')) continue

      const value = element.getAttribute(name)

      if (value) {
        selector.push(`[${name}="${escapeSelectorValue(value)}"]`)
      }
    }

    return selector.join('')
  }

  let current: HTMLElement | null = target instanceof HTMLElement ? target : target.parentElement

  while (current) {
    if (!['body', 'html'].includes(current.tagName.toLowerCase())) {
      const segment = getDataAttributeSegment(current)

      if (segment) {
        return segment
      }
    }

    current = current.parentElement
  }

  return undefined
}

export const scrubRequestInEvent = <T extends EventWithRequestUrl>(event: T) => {
  const scrubbedEvent = scrubFileNameOnObject(event) as T

  if (scrubbedEvent?.request?.url) {
    scrubbedEvent.request.url = scrubAwsFilenameFromString(scrubbedEvent.request.url)
  }

  return scrubbedEvent
}

export const scrubRequestInBreadcrumb = <T extends BreadcrumbWithUrlData>(
  breadcrumb: T,
  hint?: { event?: Event },
) => {
  const scrubbedBreadcrumb = { ...breadcrumb } as T
  const safeUiSuffix = buildSafeUiBreadcrumbSuffix(hint?.event)

  if ((breadcrumb.category === 'ui.input' || breadcrumb.category === 'ui.click') && safeUiSuffix) {
    scrubbedBreadcrumb.message = breadcrumb.message
      ? `${breadcrumb.message} ${safeUiSuffix}`
      : safeUiSuffix
  }

  if (Array.isArray(scrubbedBreadcrumb?.data?.arguments)) {
    scrubbedBreadcrumb.data.arguments = scrubbedBreadcrumb.data.arguments.map(argument =>
      scrubFileNameOnObject(argument),
    )
  }

  if (scrubbedBreadcrumb?.data?.url) {
    scrubbedBreadcrumb.data.url = scrubAwsFilenameFromString(scrubbedBreadcrumb.data.url)
  }

  if (scrubbedBreadcrumb?.data?.request?.url) {
    scrubbedBreadcrumb.data.request.url = scrubAwsFilenameFromString(
      scrubbedBreadcrumb.data.request.url,
    )
  }

  return scrubbedBreadcrumb
}
